"use client"

import Link from "next/link"
import { ChevronLeft } from "lucide-react"
import type { ReactNode } from "react"

export type Crumb = { label: string; href?: string }

export type PageHeaderProps = {
  eyebrow?: string
  title: string
  titleAccent?: string
  subtitle?: string
  rightSlot?: ReactNode
  crumbs?: Crumb[]
  backHref?: string
  backLabel?: string
  dateLabel?: string
}

export function PageHeader({
  eyebrow,
  title,
  titleAccent,
  subtitle,
  rightSlot,
  crumbs,
  backHref,
  backLabel,
  dateLabel,
}: PageHeaderProps) {
  return (
    <>
      {(crumbs || dateLabel) && (
        <header className="sticky top-0 z-20 h-[52px] bg-white/90 backdrop-blur-md border-b border-[var(--redy-border)] flex items-center px-6 gap-3">
          {crumbs && (
            <nav
              className="flex-1 flex items-center gap-2 min-w-0 text-[13px]"
              aria-label="Breadcrumb"
            >
              {crumbs.map((c, i) => {
                const isLast = i === crumbs.length - 1
                return (
                  <span
                    key={`${c.label}-${i}`}
                    className="flex items-center gap-2 min-w-0"
                  >
                    {i > 0 && (
                      <span
                        className="text-[var(--redy-border)] select-none"
                        aria-hidden
                      >
                        ·
                      </span>
                    )}
                    {c.href && !isLast ? (
                      <Link
                        href={c.href}
                        className="text-[var(--redy-muted)] hover:text-[var(--redy-charcoal)] transition-colors truncate"
                      >
                        {c.label}
                      </Link>
                    ) : (
                      <span
                        className={
                          isLast
                            ? "font-semibold text-[var(--redy-charcoal)] truncate"
                            : "text-[var(--redy-muted)] truncate"
                        }
                      >
                        {c.label}
                      </span>
                    )}
                  </span>
                )
              })}
            </nav>
          )}
          {dateLabel && (
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--redy-warm-white)] border border-[var(--redy-border)]">
              <span className="w-[6px] h-[6px] rounded-full bg-[var(--redy-success-bright)]" />
              <span className="text-[11.5px] font-medium text-[var(--redy-secondary)]">
                {dateLabel}
              </span>
            </div>
          )}
        </header>
      )}

      <section className="px-6 pt-10 pb-6 max-w-6xl mx-auto">
        {backHref && (
          <Link
            href={backHref}
            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-[var(--redy-muted)] hover:text-[var(--redy-red)] tracking-[0.04em] mb-4 transition-colors"
          >
            <ChevronLeft size={14} strokeWidth={2.25} />
            {backLabel ?? "Volver"}
          </Link>
        )}

        {eyebrow && (
          <div className="flex items-baseline gap-3 mb-3">
            <span className="text-[10.5px] font-bold tracking-[0.2em] text-[var(--redy-red)] whitespace-nowrap tabular-nums">
              {eyebrow}
            </span>
            <span className="flex-1 h-px bg-[var(--redy-border-l)]" />
          </div>
        )}

        <div className="flex items-end justify-between gap-6 flex-wrap">
          <h1
            className="text-[var(--redy-charcoal)] leading-[1.05] font-extrabold text-[28px] sm:text-[32px]"
            style={{
              fontFamily: "'Satoshi', 'Inter', system-ui, sans-serif",
              letterSpacing: "-0.02em",
            }}
          >
            {title}
            {titleAccent && (
              <>
                {" "}
                <span
                  style={{
                    background: "var(--redy-gradient)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {titleAccent}
                </span>
              </>
            )}
          </h1>
          {rightSlot && <div className="shrink-0">{rightSlot}</div>}
        </div>

        {subtitle && (
          <p className="text-sm text-[var(--redy-secondary)] mt-3 max-w-2xl leading-relaxed">
            {subtitle}
          </p>
        )}
      </section>
    </>
  )
}

// ── PageShell + PageBody — el wrapper estándar de las pages no-hub ──────────

export function PageShell({
  children,
  className = "",
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={`min-h-screen bg-[var(--redy-warm-white)] pb-bottom-nav lg:pb-12 ${className}`}
    >
      {children}
    </div>
  )
}

export function PageBody({
  children,
  wide = false,
  className = "",
}: {
  children: ReactNode
  /**
   * Ensancha el contenedor de 1152px a 1400px. Para pages que necesitan una
   * tercera columna (p.ej. el wizard de setup: rail + preguntas + preview) y
   * que con el ancho estándar dejarían la columna central impracticable.
   */
  wide?: boolean
  className?: string
}) {
  return (
    <div
      className={`px-6 pt-2 pb-10 ${wide ? "max-w-[1400px]" : "max-w-6xl"} mx-auto space-y-8 ${className}`}
    >
      {children}
    </div>
  )
}
